import * as React from "react";
import areEqual from "fbjs/lib/areEqual";

import { PicklistDataItem } from "../../../../../Engine/Core/Picklists/PicklistTypes";
import { emptyRef } from "../../../../TypingUtils";
import { reject } from "../../../../../../../Common/TypingUtils";

import { PicklistItem } from "./PicklistItem/PicklistItem";
import { Root } from "./PicklistItemList.styles";
import { PicklistColumn } from "./PicklistColumn";
import { findScrollableParent } from "./ScrollUtils";

export interface PicklistItemListProps {
    multiple: boolean;
    columns: PicklistColumn[];
    list: PicklistDataItem[];
    value: PicklistDataItem[];
    focusedItem?: undefined | PicklistDataItem;
    onChangeFocusedItem?: (item: undefined | PicklistDataItem) => void;
    onChange: (item: PicklistDataItem[]) => void;
    scrollTopOffset?: number;
    scrollBottomOffset?: number;
}

export interface PicklistItemListHandle {
    adjustScrollToViewFocusedElement(): void;
    findMostBottomAndVisibleItemIndex(): number | undefined;
    findNextPageBottomItemIndex(): number | undefined;
    findMostTopAndVisibleItemIndex(): number | undefined;
    findPrevPageTopItemIndex(): number | undefined;
}

export const PicklistItemList = React.forwardRef(
    (props: PicklistItemListProps, ref: React.Ref<PicklistItemListHandle>) => {
        const root = React.useRef<HTMLDivElement>(emptyRef);
        const focusedElement = React.useRef<HTMLDivElement>(emptyRef);

        const handleSelectItem = React.useCallback(
            (item: PicklistDataItem, checked?: boolean) => {
                if (props.multiple) {
                    const nextValue = checked ? [...props.value, item] : props.value.filter(x => !areEqual(x, item));
                    props.onChange(nextValue);
                } else {
                    const nextValue = [item];
                    props.onChange(nextValue);
                }
            },
            [props.multiple, props.onChange, props.value]
        );

        const handleMouseDown = React.useCallback(
            (item: PicklistDataItem) => props.onChangeFocusedItem?.(item),
            [props.onChangeFocusedItem]
        );

        React.useImperativeHandle(
            ref,
            () => ({
                adjustScrollToViewFocusedElement: () => {
                    if (focusedElement.current == undefined) {
                        return;
                    }
                    const scrollableParent = findScrollableParent(focusedElement.current);
                    const elementRect = focusedElement.current.getBoundingClientRect();
                    const parentRect = scrollableParent.getBoundingClientRect();

                    const topOffset = props.scrollTopOffset ?? 0;
                    const bottomOffset = props.scrollBottomOffset ?? 0;

                    if (elementRect.bottom > parentRect.bottom - bottomOffset) {
                        scrollableParent.scrollTop += elementRect.bottom - parentRect.bottom + bottomOffset;
                    } else if (elementRect.top < parentRect.top + topOffset) {
                        scrollableParent.scrollTop -= parentRect.top + topOffset - elementRect.top;
                    }
                },

                findMostBottomAndVisibleItemIndex: () => {
                    if (root.current == undefined) {
                        return undefined;
                    }
                    const scrollableParent = findScrollableParent(root.current);
                    const parentRect = scrollableParent.getBoundingClientRect();
                    const bottomOffset = props.scrollBottomOffset ?? 0;
                    const children = root.current.children;
                    if (children.length === 0) {
                        return undefined;
                    }
                    for (let i = 0; i < children.length; i++) {
                        const child = children[i] ?? reject();
                        const elementRect = child.getBoundingClientRect();

                        if (
                            elementRect.top < parentRect.bottom - bottomOffset &&
                            elementRect.bottom >= parentRect.bottom - bottomOffset
                        ) {
                            return i;
                        }
                    }
                    return children.length - 1;
                },

                findNextPageBottomItemIndex: () => {
                    if (root.current == undefined) {
                        return undefined;
                    }
                    const scrollableParent = findScrollableParent(root.current);
                    const parentRect = scrollableParent.getBoundingClientRect();
                    const bottomOffset = props.scrollBottomOffset ?? 0;
                    const topOffset = props.scrollTopOffset ?? 0;
                    const children = root.current.children;
                    if (children.length === 0) {
                        return undefined;
                    }
                    const viewportHeight = parentRect.bottom - bottomOffset - (parentRect.top + topOffset);

                    for (let i = 0; i < children.length; i++) {
                        const child = children[i] ?? reject();
                        const elementRect = child.getBoundingClientRect();

                        if (
                            elementRect.top < parentRect.bottom - bottomOffset + viewportHeight &&
                            elementRect.bottom >= parentRect.bottom - bottomOffset + viewportHeight
                        ) {
                            return i;
                        }
                    }
                    return children.length - 1;
                },

                findMostTopAndVisibleItemIndex: () => {
                    if (root.current == undefined) {
                        return undefined;
                    }
                    const scrollableParent = findScrollableParent(root.current);
                    const parentRect = scrollableParent.getBoundingClientRect();
                    const topOffset = props.scrollTopOffset ?? 0;
                    const children = root.current.children;
                    if (children.length === 0) {
                        return undefined;
                    }
                    for (let i = 0; i < children.length; i++) {
                        const child = children[i] ?? reject();
                        const elementRect = child.getBoundingClientRect();

                        if (
                            elementRect.top <= parentRect.top + topOffset &&
                            elementRect.bottom > parentRect.top + topOffset
                        ) {
                            return i;
                        }
                    }
                    return 0;
                },

                findPrevPageTopItemIndex: () => {
                    if (root.current == undefined) {
                        return undefined;
                    }
                    const scrollableParent = findScrollableParent(root.current);
                    const parentRect = scrollableParent.getBoundingClientRect();
                    const bottomOffset = props.scrollBottomOffset ?? 0;
                    const topOffset = props.scrollTopOffset ?? 0;
                    const children = root.current.children;
                    if (children.length === 0) {
                        return undefined;
                    }
                    const viewportHeight = parentRect.bottom - bottomOffset - (parentRect.top + topOffset);

                    for (let i = 0; i < children.length; i++) {
                        const child = children[i] ?? reject();
                        const elementRect = child.getBoundingClientRect();

                        if (
                            elementRect.top < parentRect.top + topOffset - viewportHeight &&
                            elementRect.bottom >= parentRect.top + topOffset - viewportHeight
                        ) {
                            return i;
                        }
                    }
                    return 0;
                },
            }),
            [focusedElement.current]
        );

        return (
            <Root ref={root}>
                {props.list.map((item, itemIndex) => (
                    <PicklistItem
                        checkable={props.multiple}
                        item={item}
                        ref={areEqual(item, props.focusedItem) ? focusedElement : null}
                        onSelect={handleSelectItem}
                        onMouseDown={handleMouseDown}
                        columns={props.columns}
                        focused={props.focusedItem === item}
                        isCurrentValue={props.value.some(x => areEqual(x, item))}
                        key={Object.values(item).join("-") + "_" + itemIndex.toString()}
                    />
                ))}
            </Root>
        );
    }
);
PicklistItemList.displayName = "PicklistItemList";
